'use client'
import {
  Close,
  Menu,
  Search,
  Switcher,
  UserAvatarFilled,
} from '@carbon/icons-react'
import type { Person } from 'app/_components/Avatar'
import Avatar from 'app/_components/Avatar'
import { Button } from 'app/_components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/_components/DropdownMenu'
import LinkItem from 'app/_components/NavigationBar/NavigationBarLinkItem'
import { NavItems } from 'app/_components/NavigationBar/navigationItems'
import TbfDigitalNextLogo from 'app/_components/svg/TbfNextLogo'
import Tag from 'app/_components/Tag'
import Link from 'next/link'
import { signOut } from 'next-auth/react'
import { useState } from 'react'

export default function NavigationBar({ user }: { user?: Person | null }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="fluid-container border-gray-10 fixed z-40 flex w-full shrink-0 justify-between overflow-hidden border-b bg-white">
      <div className="flex">
        <div className="inline-block h-full md:hidden">
          <DropdownMenu onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="nav"
                className="-ml-2 p-3.5"
                id="navbar-mobile-menu-trigger"
              >
                {open ? (
                  <Close className="size-5" title="Menü schließen" />
                ) : (
                  <Menu className="size-5" title="Menü" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-screen py-3 md:w-64">
              <DropdownMenuGroup>
                {NavItems.map((item) => (
                  <DropdownMenuItem
                    className="flex justify-start gap-2 px-4! [&_svg]:text-icon-primary"
                    asChild
                    key={item.href}
                  >
                    <LinkItem href={item.href} target={item.target}>
                      {item.icon}
                      {item.title}
                      {item.tag && <Tag color="teal">{item.tag}</Tag>}
                    </LinkItem>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <LinkItem href="/" className="px-0!">
          <TbfDigitalNextLogo className="h-5" />
        </LinkItem>
        <div className="hidden p-3 px-4 md:block">
          <div className="h-full w-px shrink-0 bg-gray-100" />
        </div>
        <nav className="hidden md:flex" aria-labelledby="primary-navigation">
          <p className="sr-only" id="primary-navigation">
            Hauptnavigation
          </p>
          {NavItems.map((item) => (
            <LinkItem key={item.href} href={item.href} target={item.target}>
              {item.icon}
              {item.title}
              {item.tag && <Tag color="teal">{item.tag}</Tag>}
            </LinkItem>
          ))}
        </nav>
      </div>

      <div className="flex gap-1">
        <DefaultSearchButton />
        <DefaultUserMenu user={user} />
        <DefaultAdditionalMenu />
      </div>
    </div>
  )
}

export function DefaultSearchButton() {
  return (
    <Link href="/search" tabIndex={-1}>
      <Button variant="nav" className="p-3.5! focus-visible:ring-inset">
        <Search className="h-5 w-5" title="Suche" />
        <Tag color="teal" className="-ml-1 hidden md:block">
          Beta
        </Tag>
      </Button>
    </Link>
  )
}

export function DefaultUserMenu({ user }: { user?: Person | null }) {
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="nav"
          className="p-3.5! focus-visible:ring-inset"
          title="User-Menü"
          id="navbar-user-menu"
        >
          {open ? (
            <Close className="h-5 w-5" title="User-Menü schließen" />
          ) : (
            <>
              {user ? (
                <Avatar employee={user} className="h-5 w-5" />
              ) : (
                <UserAvatarFilled className="h-5 w-5" />
              )}
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-screen md:w-64">
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link href={`https://tbf.digital/user/${user?.alias}`}>
              Mein Profil
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => signOut()}>Logout</DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function DefaultAdditionalMenu() {
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="nav"
          className="-mr-2 p-3.5! focus-visible:ring-inset"
          id="navbar-additional-menu-button"
        >
          {open ? (
            <Close className="h-5 w-5" title="App Menü schließen" />
          ) : (
            <Switcher className="h-5 w-5" title="App Menü" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="text-text-secondary h-[calc(100vh-55px)] w-screen overflow-y-auto py-3 md:w-64">
        <DropdownMenuSeparator title="Tools" />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://toolradar.tbf.digital/" target="_blank">
              Tool-Radar
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://tbf.digital/onepagers/all">OnePager Tool</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://tbf.digital/fileshare">Dokumente teilen</Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="h-8">
            <Link href="https://chatbot.app.tbf.ch/">KI-Chat</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://pw.tbf.ch/" target="_blank">
              Passwort verwalten
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="mb-9 h-8">
            <Link href="https://snappass.app.tbf.ch" target="_blank">
              Passwort teilen
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator
          className="text-text-helper"
          title="Netzwerk-Service"
        />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://netzwerkservices.tbf.ch/" target="_blank">
              Anfrage-Formular
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="mb-9 h-8">
            <Link href="/stories/cmde7xstikwwko301eqkfjmz1">Wer wir sind</Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator title="IT-Support" />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://ticketing.tbf.ch/" target="_blank">
              Ticketing-System
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://status.app.tbf.ch" target="_blank">
              Systemstatus
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="h-8">
            <Link href="https://next.tbf.digital/stories/cmdfpktzv2v58nv01myivu47h">
              WiFi-Gastzugang
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="mb-9 h-8">
            <Link
              href="https://teams.microsoft.com/l/team/19%3ab96dadd830b64a988bc91e09ac218e34%40thread.skype/conversations?groupId=0fe2f43b-84ac-438d-af28-b7c44960b3e5&tenantId=a9da9e3c-a0c6-4f03-bb67-6af6ad7abac9"
              target="_blank"
            >
              Austausch und Support
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator title="tbf.digital Weiterentwicklung" />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="mb-9 h-8">
            <Link href="/changelog">tbf-digital Changelog</Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
